/**
 * P4 — past-tense record of the card service fee that was charged on a deposit.
 *
 * Renders ONLY what the server already decided: `serviceFee.show` is computed by
 * `getServiceFeeDisclosure` (a COLLECTED card deposit whose capture payment
 * carries a fee) and `chargedAmount` is read off the `Payment` the fee
 * was charged on. Never recompute from current settings here — the amount is
 * history, and settings move.
 *
 * This is NOT the pre-payment disclosure beside "Pay by card"; that one lives in
 * DepositCTA and is driven by `depositSurcharge`. Keep the two separate.
 *
 * Shared because approve-then-pay means the fee only exists AFTER approval, so
 * the note has to render both in the live quote body and in the approved/signed
 * view's deposit panel (which returns before the body ever renders).
 */
import {
  formatDiscountPercent,
  surchargeToDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';

export interface ServiceFeeNoteProps {
  serviceFee?: { show: boolean; percent: number; chargedAmount?: number | null } | null;
  className?: string;
}

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function ServiceFeeNote({
  serviceFee,
  className = 'text-[11px] text-[#6B7280]',
}: ServiceFeeNoteProps) {
  // A charged amount is history and prints even if the tenant has since turned
  // surcharging off (percent 0). Requiring the percent unconditionally hid a
  // fee that WAS charged; the rate only matters for the amount-less fallback.
  if (!serviceFee?.show) return null;
  if (!serviceFee.chargedAmount && !(serviceFee.percent > 0)) return null;

  return (
    <p className={className}>
      {serviceFee.chargedAmount
        ? `This deposit used the card price. ACH/check savings not applied: ${fmt(serviceFee.chargedAmount)}.`
        : `This deposit used the card price; the equivalent ${formatDiscountPercent(
            surchargeToDiscountPercent(serviceFee.percent),
          )} ACH/check discount was not applied.`}
    </p>
  );
}
